import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, type RaRecord } from 'react-admin';
import { Box, Button, Chip, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { useFeature } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { AssignDialog, AssignmentsTable, EndAssignmentDialog, HistoryTable, ReasonDialog } from './AssignmentParts';
import {
  FEATURE_FUEL_SURCHARGES,
  fetchAssignments,
  fetchHistory,
  fetchTypes,
  plDay,
  withdrawAssignment,
  type FuelSurchargeAssignment,
  type FuelSurchargeChange,
  type FuelSurchargeType,
} from './fuelSurchargesApi';

/**
 * „Korekta paliwowa” na karcie kontrahenta (makieta back office v2, fala B,
 * B4; BR-CS-12: reguła klienta z właścicielem i datą obowiązywania;
 * przełącznik `fuelSurcharges`, 21.10.2026).
 *
 * Obowiązująca dziś korekta, wszystkie przypisania z datami i historia
 * zmian kontrahenta. Przyjęcie zleceń (uprawnienie „Przyjęcie zleceń”)
 * przypisuje, zmienia datę końca i wycofuje — każda zmiana z powodem.
 * Reszta tylko ogląda. Wyłączony przełącznik — sekcji nie ma.
 */

/** Przypisanie obowiązujące w dniu `today` (`YYYY-MM-DD`), bez wycofanych. */
export function currentAssignment(
  rows: FuelSurchargeAssignment[],
  today: string,
): FuelSurchargeAssignment | null {
  return (
    rows.find((a) => !a.withdrawnAt && a.validFrom <= today && (a.validTo === null || today <= a.validTo)) ??
    null
  );
}

const warsawToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );

const ContractorFuelSurchargesPanel = ({ contractor }: { contractor: { id: number; legalName: string } }) => {
  const notify = useNotify();
  const canEdit = useHasPermission('ORDER_INTAKE');
  const [rows, setRows] = useState<FuelSurchargeAssignment[] | null>(null);
  const [history, setHistory] = useState<FuelSurchargeChange[]>([]);
  const [types, setTypes] = useState<FuelSurchargeType[]>([]);
  const [withdrawn, setWithdrawn] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [ending, setEnding] = useState<FuelSurchargeAssignment | null>(null);
  const [withdrawing, setWithdrawing] = useState<FuelSurchargeAssignment | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, h, t] = await Promise.all([
        fetchAssignments({ contractorId: contractor.id, withdrawn }),
        fetchHistory({ contractorId: contractor.id }),
        fetchTypes(),
      ]);
      setRows(a);
      setHistory(h);
      setTypes(t);
    } catch (e) {
      setRows([]);
      notify(toErrorMessage(e), { type: 'error' });
    }
  }, [contractor.id, withdrawn, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = rows ? currentAssignment(rows, warsawToday()) : null;
  const typeName = (id: number | null) => types.find((t) => t.id === id)?.name ?? (id ? `#${id}` : '—');

  return (
    <SectionCard title="Korekta paliwowa">
      <Box data-testid="contractor-fuel-surcharges">
        {rows === null ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
              {current ? (
                <Chip color="info" size="small" label={`dziś: ${current.type.name}`} data-testid="contractor-fuel-current" />
              ) : (
                <Chip variant="outlined" size="small" label="dziś: bez korekty" data-testid="contractor-fuel-current" />
              )}
              {current ? (
                <Typography variant="caption" color="text.secondary">
                  od {plDay(current.validFrom)}
                  {current.validTo ? ` do ${plDay(current.validTo)}` : ', bez daty końca'}
                </Typography>
              ) : null}
              <Box sx={{ flex: 1 }} />
              <FormControlLabel
                control={<Switch size="small" checked={withdrawn} onChange={(e) => setWithdrawn(e.target.checked)} />}
                label="Pokaż wycofane"
              />
              {canEdit && (
                <Button variant="outlined" size="small" onClick={() => setAssigning(true)}>
                  Przypisz korektę
                </Button>
              )}
            </Stack>
            <AssignmentsTable
              rows={rows}
              canEdit={canEdit}
              showContractor={false}
              onEnd={setEnding}
              onWithdraw={setWithdrawing}
            />
            <Button size="small" sx={{ mt: 1 }} onClick={() => setShowHistory((v) => !v)}>
              {showHistory ? 'Ukryj historię zmian' : `Historia zmian (${history.length})`}
            </Button>
            {showHistory && <HistoryTable rows={history} typeName={typeName} />}
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
              Kontrahent ma jedną korektę naraz. Wartości korekt przyjdą od CS; na zleceniach korekta jest
              tylko informacją — nie zmienia marży ani kosztu.
            </Typography>
          </>
        )}
      </Box>
      <AssignDialog
        open={assigning}
        onClose={() => setAssigning(false)}
        onSaved={() => {
          setAssigning(false);
          void load();
        }}
        types={types}
        contractor={contractor}
      />
      <EndAssignmentDialog
        assignment={ending}
        onClose={() => setEnding(null)}
        onSaved={() => {
          setEnding(null);
          void load();
        }}
      />
      <ReasonDialog
        open={withdrawing !== null}
        title="Wycofaj przypisanie"
        text="Wycofanie to poprawka pomyłki — przypisanie zostaje w historii, ale przestaje obowiązywać. Zwykłą zmianę korekty od jakiejś daty zrób nowym przypisaniem."
        confirmLabel="Wycofaj"
        onClose={() => setWithdrawing(null)}
        onConfirm={async (reason) => {
          if (!withdrawing) return;
          try {
            await withdrawAssignment(withdrawing.id, reason);
            notify('Przypisanie wycofane', { type: 'success' });
            setWithdrawing(null);
            void load();
          } catch (e) {
            notify(toErrorMessage(e), { type: 'error' });
          }
        }}
      />
    </SectionCard>
  );
};

export const ContractorFuelSurcharges = () => {
  const r = useRecordContext<RaRecord>();
  const on = useFeature(FEATURE_FUEL_SURCHARGES);
  if (!on || !r) return null;
  return (
    <ContractorFuelSurchargesPanel
      key={String(r.id)}
      contractor={{ id: Number(r.id), legalName: String(r.legalName ?? '') }}
    />
  );
};
